<template>
  <div>
    <div class="p-20 flex">
      <div class="box">
        <h4 v-if="title" class="mv-0">{{title}}</h4>
        <h4 v-else class="mv-0">{{ $t('dashboards.package-dashboard') }}</h4>
      </div>
      <div class="box center"></div>
      <div class="box self-flex-end dashboard-actions">
        <el-tooltip :content="$t('common.Refresh')" placement="top">
          <el-button :icon="loadingIcon" size="small" class="dashboard-action-btn" @click="refresh"></el-button>
        </el-tooltip>
        <el-tooltip v-if="id!=null" :content="$t('common.Configure')" placement="top">
          <el-button icon="el-icon-edit-outline" size="small" class="dashboard-action-btn" @click="handleConfigure"></el-button>
        </el-tooltip>
        <el-tooltip v-if="id!=null" :content="$t('common.Delete')" placement="top">
          <el-button icon="el-icon-delete" type="danger" plain size="small" class="dashboard-action-btn" @click="handleDelete"></el-button>
        </el-tooltip>
      </div>
    </div>
    <el-skeleton :count="1" animated :loading="loading">
      <template slot="template">
        <tableSkeleton :rows="parseInt((height-250)/40) || 1" :height="height-250" :columnLabel="skeletonColumns"></tableSkeleton>
      </template>
      <template>
        <el-table
          :data="packages"
          stripe
          :height="height-250"
          :empty-text="$t('packages.noPackagesGenerated')"
          style="width: 100%">
          <el-table-column
            :label="$t('packages.packgeNumber')"
            min-width="180"
            show-overflow-tooltip>
            <template slot-scope="scope">
              <PortausLink
                v-if="getSalesOrderId(scope.row) != null"
                :id="getSalesOrderId(scope.row)"
                path="/sales-orders/"
                :query="{ activeTab: 'packages' }"
                :label="scope.row.pkNumber"></PortausLink>
              <span v-else>{{ scope.row.pkNumber }}</span>
            </template>
          </el-table-column>
          <el-table-column
            :label="$t('packages.inventory')"
            min-width="160"
            show-overflow-tooltip>
            <template slot-scope="scope">
              <span>{{ getPackageInventoryName(scope.row) }}</span>
            </template>
          </el-table-column>
          <el-table-column
            :label="$t('packages.deliveryDate')"
            width="150">
            <template slot-scope="scope">
              <span v-if="scope.row.expectedDeliveryDate">{{ formatDate(scope.row.expectedDeliveryDate) }}</span>
              <span v-else> -- </span>
            </template>
          </el-table-column>
          <el-table-column
            :label="$t('packages.externalReferenceNumber')"
            min-width="170"
            show-overflow-tooltip>
            <template slot-scope="scope">
              <span>{{ scope.row.externalNumber || '--' }}</span>
            </template>
          </el-table-column>
          <el-table-column
            :label="$t('packages.confirmationNumber')"
            min-width="150"
            show-overflow-tooltip>
            <template slot-scope="scope">
              <span>{{ scope.row.confirmationNumber || '--' }}</span>
            </template>
          </el-table-column>
          <el-table-column
            :label="$t('packages.status')"
            width="150"
            show-overflow-tooltip>
            <template slot-scope="scope">
              <el-tag v-if="scope.row.status">{{$t('packages.statuses.' + scope.row.status.code)}}</el-tag>
              <span v-else> -- </span>
            </template>
          </el-table-column>
        </el-table>
      </template>
    </el-skeleton>
    <div class="m-20 flex justify-space-between" style="display:absolute; top:20px;">
      <h4 class="mt-5 mb-0 o-050 fs-14 secondray-text">{{count}} {{ $t('packages.packagesFoundOutOf') }} {{totalCount}}</h4>
      <Pagination
        class="mt-5 mb-0 fs-14 secondary-text"
        :current-page="page"
        :total-number-of-page="totalPages"
        @click-previous="clickPrevious"
        @click-next="clickNext" />
    </div>
  </div>
</template>
<script>
import packageAPI from '@/services/API/package';
import tableSkeleton from '@/components/skeleton/table-skeleton';
import Pagination from '@/components/pagination.vue';
import PortausLink from '@/components/portaus-link';
import { mapActions, mapGetters } from 'vuex';

export default {
  name: 'package-dashboard-card',
  props: {
    uuid: {
      type: String
    },
    id: {
      type: Number
    },
    dashboard: {
      type: Object,
      default: null
    },
    position: {
      type: Number,
      required: true
    },
    filters: {
      type: Object,
      default: () => ({})
    },
    title: {
      type: String,
      default: null
    },
    inventories: {
      type: Array,
      default: () => []
    },
    height: {
      type: Number
    }
  },
  data() {
    return {
      packages: [],
      loading: false,
      timer: '',
      totalCount: 0,
      page: 1,
      totalPages: 1,
    };
  },
  components: {
    tableSkeleton,
    Pagination,
    PortausLink,
  },
  computed: {
    loadingIcon() {
      return this.loading ? 'el-icon-loading' : 'el-icon-refresh';
    },
    count() {
      return this.packages == null ? 0 : this.packages.length;
    },
    skeletonColumns() {
      return [
        { label: ' ', width: 50, sortable: false },
        { label: this.$t('packages.packgeNumber'), width: 150, sortable: true },
        { label: this.$t('packages.inventory'), width: 150, sortable: true },
        { label: this.$t('packages.deliveryDate'), width: 140, sortable: true },
        { label: this.$t('packages.externalReferenceNumber'), width: 160, sortable: true },
        { label: this.$t('packages.confirmationNumber'), width: 150, sortable: true },
        { label: this.$t('packages.status'), width: 130, sortable: true },
      ];
    },
    ...mapGetters('inventory', ['getInventory'])
  },
  methods: {
    formatDate(value) {
      return this.$moment != null ? this.$moment(value).format('L') : new Date(value).toLocaleDateString();
    },
    getSalesOrderId(packageRow) {
      return _.get(packageRow, 'salesOrder.id') || null;
    },
    getPackageInventoryName(packageRow) {
      return _.get(packageRow, 'inventory.name') || _.get(this.getInventory(_.get(packageRow, 'inventory.id')), 'name') || '--';
    },
    normalizePackagesResponse(packagesResponse) {
      if (Array.isArray(packagesResponse)) {
        return {
          list: packagesResponse,
          count: packagesResponse.length,
          pages: 1,
        };
      }

      return {
        list: _.get(packagesResponse, 'list', []),
        count: _.get(packagesResponse, 'count', _.get(packagesResponse, 'list.length', 0)),
        pages: _.get(packagesResponse, 'pages', 1),
      };
    },
    handleConfigure() {
      this.$emit('configure', this.dashboard);
    },
    handleDelete() {
      this.deleteDashboard(this.id)
        .then(() => {
          this.$message({
            message: 'Le canal a été supprimé avec succès.',
            type: 'success'
          });
        });
    },
    clickPrevious() {
      if (this.page > 1) {
        this.page -= 1;
      }
      this.refresh();
    },
    clickNext() {
      if (this.page < this.totalPages) {
        this.page += 1;
      }
      this.refresh();
    },
    refresh() {
      this.cancelAutoUpdate();
      this.fetchPackages()
        .then(() => this.createTimer());
    },
    fetchPackages() {
      this.loading = true;
      const params = {
        page: this.page,
        limit: 50,
      };

      const selectedStatuses = _.chain(this.filters)
        .pickBy((value) => value === true)
        .keys()
        .value();

      if (!_.isEmpty(selectedStatuses)) {
        params.status = selectedStatuses;
      }

      if (!_.isEmpty(this.inventories)) {
        params.inventoryId = _.map(this.inventories, inv => inv.id);
      }

      return packageAPI.fetch(params)
        .then((packagesResponse) => {
          const normalizedResponse = this.normalizePackagesResponse(packagesResponse);
          this.packages = normalizedResponse.list;
          this.totalCount = normalizedResponse.count;
          this.totalPages = normalizedResponse.pages;
          this.updateTotalElement({ uuid: this.uuid, totalElement: this.totalCount });
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
          this.packages = [];
          this.totalCount = 0;
          this.totalPages = 1;
        });
    },
    createTimer() {
      this.timer = setInterval(this.fetchPackages, 60000 + (this.position * 2000));
    },
    cancelAutoUpdate() {
      clearInterval(this.timer);
    },
    ...mapActions('genericDashboards', ['updateTotalElement', 'deleteDashboard']),
  },
  created() {
    this.fetchPackages()
      .then(() => this.createTimer());
  },
  beforeDestroy() {
    this.cancelAutoUpdate();
  }
}
</script>
<style lang="scss" scoped>

</style>